import { useEffect, useMemo, useState } from "react";
import {
  DollarSign,
  TrendingUp,
  Banknote,
  Receipt,
  Settings as SettingsIcon,
  Download,
  Plus,
  Trash2,
  PiggyBank,
  Wallet,
  Calculator,
  CalendarRange,
  Users,
  Briefcase,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import FinancePlanning from "./FinancePlanning";

const fmtAUD = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
const fmtAUD2 = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Number(n || 0));
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-AU", {
    month: "short",
    year: "2-digit",
  });
};

const KpiCard = ({ icon: Icon, label, value, hint, color = "#87CEEB" }) => (
  <div
    className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm"
    data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div
        className="h-10 w-10 rounded-xl flex items-center justify-center"
        style={{ background: `${color}22`, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </span>
    </div>
    <div className="text-3xl font-extrabold text-slate-900 leading-tight">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

const SectionTitle = ({ children, hint }) => (
  <div className="mb-4 mt-2">
    <h3 className="text-lg font-bold text-slate-900">{children}</h3>
    {hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}
  </div>
);

export default function AdminFinance() {
  const [tab, setTab] = useState("dashboard");
  const [settings, setSettings] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fyStartYear, setFyStartYear] = useState(null);

  const loadAll = async (year) => {
    setLoading(true);
    try {
      const yearQ = year ? `?fy_start_year=${year}` : "";
      const [s, d, p, f, c, e] = await Promise.all([
        authFetch(`${API}/admin/finance/settings`).then((r) => r.json()),
        authFetch(`${API}/admin/finance/dashboard`).then((r) => r.json()),
        authFetch(`${API}/admin/finance/pnl${yearQ}`).then((r) => r.json()),
        authFetch(`${API}/admin/finance/forecast${yearQ}`).then((r) => r.json()),
        authFetch(`${API}/admin/finance/cashflow?days=90`).then((r) => r.json()),
        authFetch(
          `${API}/admin/finance/expenses${year ? `?fy_start_year=${year}` : ""}`
        ).then((r) => r.json()),
      ]);
      setSettings(s);
      setDashboard(d);
      setPnl(p);
      setForecast(f);
      setCashflow(c);
      setExpenses(e.expenses || []);
      setExpenseCategories(e.categories || []);
      if (!fyStartYear && p?.fy_start_year) setFyStartYear(p.fy_start_year);
    } catch (err) {
      console.error("[finance] load failed", err);
      toast.error("Could not load finance data");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const fyOptions = useMemo(() => {
    const now = new Date();
    const startMonth = settings?.fy_start_month || 7;
    const baseY =
      now.getMonth() + 1 >= startMonth ? now.getFullYear() : now.getFullYear() - 1;
    return [baseY - 1, baseY, baseY + 1];
  }, [settings]);

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Crunching the numbers…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-finance">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Finance</h2>
          <p className="text-sm text-slate-500">
            {dashboard.fy_label} · all figures in {dashboard.currency} ·
            updated {new Date(dashboard.as_of).toLocaleString("en-AU")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={fyStartYear || ""}
            onChange={(e) => {
              const y = Number(e.target.value);
              setFyStartYear(y);
              loadAll(y);
            }}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white"
            data-testid="finance-fy-select"
          >
            {fyOptions.map((y) => (
              <option key={y} value={y}>
                FY{y + 1}
              </option>
            ))}
          </select>
          <a
            href={`${API}/admin/finance/export/invoices.csv${fyStartYear ? `?fy_start_year=${fyStartYear}` : ""}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
            data-testid="export-invoices-csv"
          >
            <Download className="w-3.5 h-3.5" /> Invoices CSV (Xero)
          </a>
          <a
            href={`${API}/admin/finance/export/pnl.csv${fyStartYear ? `?fy_start_year=${fyStartYear}` : ""}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
            data-testid="export-pnl-csv"
          >
            <Download className="w-3.5 h-3.5" /> P&L CSV
          </a>
          <a
            href={`${API}/admin/finance/export/worker-payments.csv${fyStartYear ? `?fy_start_year=${fyStartYear}` : ""}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
            data-testid="export-payroll-csv"
          >
            <Download className="w-3.5 h-3.5" /> Worker Payments CSV
          </a>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-xl p-1 mb-2 flex flex-wrap gap-1 h-auto justify-start w-full">
          <TabsTrigger value="dashboard" className="rounded-lg" data-testid="finance-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="planning" className="rounded-lg" data-testid="finance-tab-planning">Planning</TabsTrigger>
          <TabsTrigger value="pnl" className="rounded-lg" data-testid="finance-tab-pnl">P&L</TabsTrigger>
          <TabsTrigger value="forecast" className="rounded-lg" data-testid="finance-tab-forecast">Forecast vs Actuals</TabsTrigger>
          <TabsTrigger value="cashflow" className="rounded-lg" data-testid="finance-tab-cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-lg" data-testid="finance-tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-lg" data-testid="finance-tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* ──────── Dashboard ──────── */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={DollarSign} label="Revenue FYTD" value={fmtAUD(dashboard.fytd.revenue)} hint={`${dashboard.fytd.hours.toLocaleString()} hrs · ${dashboard.fytd.bookings} bookings`} />
            <KpiCard icon={Wallet} label="Paid FYTD" value={fmtAUD(dashboard.fytd.paid)} color="#10B981" hint="Already in the bank" />
            <KpiCard icon={Receipt} label="Outstanding" value={fmtAUD(dashboard.fytd.unpaid)} color="#F59E0B" hint={`${dashboard.outstanding_invoices_count} invoices unpaid`} />
            <KpiCard icon={PiggyBank} label="GST Owed FYTD" value={fmtAUD(dashboard.gst_owed_fytd)} color="#7B61FF" hint={`${Math.round((settings?.gst_rate || 0.1) * 100)}% of ex-GST revenue`} />
            <KpiCard icon={TrendingUp} label="Revenue MTD" value={fmtAUD(dashboard.mtd.revenue)} hint={`${dashboard.mtd.bookings} bookings · ${dashboard.mtd.hours} hrs`} />
            <KpiCard icon={Calculator} label="Avg Shift Value" value={fmtAUD(dashboard.average_shift_value)} hint="This month" />
            <KpiCard icon={Users} label="Active Participants" value={dashboard.active_participants.toLocaleString()} color="#0EA5E9" hint="All time" />
            <KpiCard icon={Briefcase} label="Approved Workers" value={dashboard.active_workers.toLocaleString()} color="#0EA5E9" hint="Verified subcontractors" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white border border-slate-100 p-5">
              <SectionTitle hint="By total spend this FY">Top participants</SectionTitle>
              <div className="space-y-2">
                {(dashboard.top_participants || []).map((p) => (
                  <div key={p.user_id} className="flex items-center justify-between text-sm border-b border-slate-50 last:border-0 py-2">
                    <div>
                      <div className="font-semibold text-slate-900">{p.name || p.email || "—"}</div>
                      <div className="text-xs text-slate-400">{p.bookings} bookings</div>
                    </div>
                    <div className="font-bold text-slate-900">{fmtAUD(p.spend)}</div>
                  </div>
                ))}
                {!dashboard.top_participants?.length && (
                  <div className="text-sm text-slate-400 py-3">No participant revenue yet this FY.</div>
                )}
              </div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-100 p-5">
              <SectionTitle hint="By revenue brought in this FY">Top workers</SectionTitle>
              <div className="space-y-2">
                {(dashboard.top_workers || []).map((w) => (
                  <div key={w.worker_id} className="flex items-center justify-between text-sm border-b border-slate-50 last:border-0 py-2">
                    <div>
                      <div className="font-semibold text-slate-900">{w.name || "—"}</div>
                      <div className="text-xs text-slate-400">{w.bookings} shifts · {w.hours.toFixed(1)} hrs</div>
                    </div>
                    <div className="font-bold text-slate-900">{fmtAUD(w.revenue)}</div>
                  </div>
                ))}
                {!dashboard.top_workers?.length && (
                  <div className="text-sm text-slate-400 py-3">No worker revenue yet this FY.</div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ──────── P&L ──────── */}
        <TabsContent value="pnl">
          <PnLTable pnl={pnl} />
        </TabsContent>

        {/* ──────── Planning (charts + budgets) ──────── */}
        <TabsContent value="planning">
          <FinancePlanning categories={expenseCategories} onChange={() => loadAll(fyStartYear)} />
        </TabsContent>

        {/* ──────── Forecast ──────── */}
        <TabsContent value="forecast">
          <ForecastTable forecast={forecast} onReload={() => loadAll(fyStartYear)} fyStartYear={fyStartYear} />
        </TabsContent>

        {/* ──────── Cash flow ──────── */}
        <TabsContent value="cashflow">
          <CashflowPanel cashflow={cashflow} />
        </TabsContent>

        {/* ──────── Expenses ──────── */}
        <TabsContent value="expenses">
          <ExpensesPanel
            expenses={expenses}
            categories={expenseCategories}
            onReload={() => loadAll(fyStartYear)}
          />
        </TabsContent>

        {/* ──────── Settings ──────── */}
        <TabsContent value="settings">
          <SettingsPanel settings={settings} onSaved={() => loadAll(fyStartYear)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────────────────────────── P&L Table ─────────────────────────────── */
function PnLTable({ pnl }) {
  if (!pnl) return null;
  const t = pnl.totals;
  return (
    <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Profit & Loss — {pnl.fy_label}</h3>
          <p className="text-sm text-slate-500">Revenue is live from completed bookings. Expenses come from the Expenses tab.</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">Net retained</div>
          <div className="text-2xl font-extrabold text-slate-900">{fmtAUD(t.retained_earnings)}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Month</th>
              <th className="text-right px-3 py-3">Hours</th>
              <th className="text-right px-3 py-3">Revenue</th>
              <th className="text-right px-3 py-3">Ex-GST</th>
              <th className="text-right px-3 py-3">GST</th>
              <th className="text-right px-3 py-3">Expenses</th>
              <th className="text-right px-3 py-3">Profit</th>
              <th className="text-right px-3 py-3">Director Wages</th>
              <th className="text-right px-3 py-3">Company Tax</th>
              <th className="text-right px-3 py-3">Retained</th>
              <th className="text-right px-3 py-3">Margin</th>
            </tr>
          </thead>
          <tbody>
            {pnl.months.map((m) => (
              <tr key={m.month} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-semibold text-slate-900">{monthLabel(m.month)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{m.hours.toFixed(1)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{fmtAUD2(m.revenue)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{fmtAUD2(m.revenue_ex_gst)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{fmtAUD2(m.gst_collected)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtAUD2(m.expenses)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${m.profit < 0 ? "text-rose-600" : "text-slate-900"}`}>{fmtAUD2(m.profit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtAUD2(m.director_wages)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtAUD2(m.company_tax)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${m.retained_earnings < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmtAUD2(m.retained_earnings)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{m.margin_pct}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-900 text-white">
              <td className="px-4 py-3 font-bold">Total {pnl.fy_label}</td>
              <td className="px-3 py-3 text-right tabular-nums">{t.hours.toFixed(1)}</td>
              <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtAUD2(t.revenue)}</td>
              <td className="px-3 py-3 text-right tabular-nums">—</td>
              <td className="px-3 py-3 text-right tabular-nums">{fmtAUD2(t.gst_collected)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{fmtAUD2(t.expenses)}</td>
              <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtAUD2(t.profit)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{fmtAUD2(t.director_wages)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{fmtAUD2(t.company_tax)}</td>
              <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtAUD2(t.retained_earnings)}</td>
              <td className="px-3 py-3 text-right tabular-nums">{t.margin_pct}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Forecast ─────────────────────────────── */
function ForecastTable({ forecast, onReload, fyStartYear }) {
  const [starting, setStarting] = useState(forecast?.assumptions?.starting_clients ?? 1);
  const [growth, setGrowth] = useState(forecast?.assumptions?.monthly_new_clients ?? 1);
  const [running, setRunning] = useState(false);

  if (!forecast) return null;

  const recompute = async () => {
    setRunning(true);
    try {
      const q = new URLSearchParams({
        starting_clients: String(starting),
        monthly_new_clients: String(growth),
      });
      if (fyStartYear) q.set("fy_start_year", String(fyStartYear));
      await authFetch(`${API}/admin/finance/forecast?${q.toString()}`)
        .then((r) => r.json());
      onReload();
      toast.success("Forecast recalculated");
    } catch (err) {
      console.error(err);
      toast.error("Could not recalculate forecast");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white border border-slate-100 p-5 flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs">Starting clients</Label>
          <Input
            type="number"
            value={starting}
            min={0}
            onChange={(e) => setStarting(Number(e.target.value))}
            className="h-10 w-32"
            data-testid="forecast-starting-clients"
          />
        </div>
        <div>
          <Label className="text-xs">New clients/month</Label>
          <Input
            type="number"
            value={growth}
            min={0}
            onChange={(e) => setGrowth(Number(e.target.value))}
            className="h-10 w-32"
            data-testid="forecast-monthly-new-clients"
          />
        </div>
        <Button
          onClick={recompute}
          disabled={running}
          className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
          data-testid="forecast-recompute"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : "Recalculate"}
        </Button>
        <div className="text-xs text-slate-500 ml-auto">
          Rate used: <strong>{fmtAUD(forecast.assumptions.hourly_rate)}/hr</strong> ·
          {forecast.assumptions.hours_per_client_week} hrs/wk per client
          <span className="ml-2 text-slate-400">(change in Settings)</span>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Month</th>
                <th className="text-right px-3 py-3">Cumulative clients</th>
                <th className="text-right px-3 py-3">Forecast hrs</th>
                <th className="text-right px-3 py-3">Forecast revenue</th>
                <th className="text-right px-3 py-3">Actual hrs</th>
                <th className="text-right px-3 py-3">Actual revenue</th>
                <th className="text-right px-3 py-3">Variance</th>
                <th className="text-right px-3 py-3">%</th>
              </tr>
            </thead>
            <tbody>
              {forecast.months.map((m) => {
                const positive = m.variance >= 0;
                return (
                  <tr key={m.month} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 font-semibold text-slate-900">{monthLabel(m.month)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{m.cumulative_clients}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{m.forecast_hours}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtAUD2(m.forecast_revenue)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{m.actual_hours}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{fmtAUD2(m.actual_revenue)}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${positive ? "text-emerald-700" : "text-rose-600"}`}>
                      {positive ? "+" : ""}
                      {fmtAUD2(m.variance)}
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${positive ? "text-emerald-700" : "text-rose-600"}`}>
                      {positive ? "+" : ""}
                      {m.variance_pct}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Cash Flow ─────────────────────────────── */
function CashflowPanel({ cashflow }) {
  if (!cashflow) return null;
  const inT = cashflow.expected_cash_in;
  const out = cashflow.expected_cash_out;
  const positive = cashflow.net >= 0;
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white border border-slate-100 p-5">
          <div className="text-sm text-slate-500">Cash in</div>
          <div className="mt-2 text-3xl font-extrabold text-emerald-600">{fmtAUD(inT.total)}</div>
          <div className="mt-3 text-sm text-slate-600 space-y-1">
            <div className="flex justify-between"><span>Unpaid invoices ({inT.unpaid_invoices_count})</span><span className="font-semibold">{fmtAUD2(inT.unpaid_invoices_total)}</span></div>
            <div className="flex justify-between"><span>Scheduled bookings ({inT.future_scheduled_bookings_count})</span><span className="font-semibold">{fmtAUD2(inT.future_scheduled_revenue)}</span></div>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-100 p-5">
          <div className="text-sm text-slate-500">Cash out (next {cashflow.horizon_days} days)</div>
          <div className="mt-2 text-3xl font-extrabold text-rose-600">{fmtAUD(out.projected_total)}</div>
          <div className="mt-3 text-sm text-slate-600 space-y-1">
            <div className="flex justify-between"><span>Avg monthly expense (last 3m)</span><span className="font-semibold">{fmtAUD2(out.average_monthly_expense)}</span></div>
            <div className="flex justify-between text-xs text-slate-400"><span>Recurring spend projection only — capex excluded</span></div>
          </div>
        </div>
        <div className={`rounded-2xl border p-5 ${positive ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <div className="text-sm text-slate-600">Net cash · next {cashflow.horizon_days} days</div>
          <div className={`mt-2 text-4xl font-extrabold ${positive ? "text-emerald-700" : "text-rose-700"}`}>{fmtAUD(cashflow.net)}</div>
          <div className="mt-3 text-xs text-slate-500">
            Based on unpaid invoices + scheduled bookings minus average recurring spend.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Expenses ─────────────────────────────── */
function ExpensesPanel({ expenses, categories, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    date: new Date().toISOString().slice(0, 10),
    category: categories[0] || "Miscellaneous",
    amount: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const totalsByMonth = useMemo(() => {
    const map = {};
    for (const e of expenses) {
      map[e.month] = (map[e.month] || 0) + Number(e.amount || 0);
    }
    return map;
  }, [expenses]);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be > 0");
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/finance/expenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          month: form.date ? form.date.slice(0, 7) : form.month,
          date: form.date || null,
          category: form.category,
          amount: Number(form.amount),
          notes: form.notes || null,
        }),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success("Expense saved");
      setOpen(false);
      setForm({ ...form, amount: "", notes: "" });
      onReload();
    } catch (err) {
      console.error(err);
      toast.error("Could not save expense");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    const r = await authFetch(`${API}/admin/finance/expenses/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Expense deleted"); onReload(); }
    else toast.error("Could not delete");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Expenses</h3>
          <p className="text-sm text-slate-500">Add monthly business expenses. They feed straight into the P&L tab.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-1" /> Add expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add expense</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" />
                </div>
                <div>
                  <Label>Amount (AUD)</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  data-testid="exp-category"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Invoice ref, supplier, etc." data-testid="exp-notes" />
              </div>
              <Button onClick={submit} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="exp-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save expense"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Month</th>
              <th className="text-left px-3 py-3">Category</th>
              <th className="text-right px-3 py-3">Amount</th>
              <th className="text-left px-3 py-3">Notes</th>
              <th className="text-right px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr><td colSpan="5" className="text-center text-slate-400 py-8">No expenses yet — click <strong>Add expense</strong> to start.</td></tr>
            )}
            {expenses.map((e) => (
              <tr key={e.expense_id} className="border-b border-slate-50">
                <td className="px-4 py-2.5 font-semibold">{monthLabel(e.month)}</td>
                <td className="px-3 py-2.5">{e.category}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmtAUD2(e.amount)}</td>
                <td className="px-3 py-2.5 text-slate-500">{e.notes || "—"}</td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => remove(e.expense_id)} className="text-rose-600 hover:text-rose-700" data-testid={`exp-del-${e.expense_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {expenses.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan="2" className="px-4 py-3 font-bold">Months recorded: {Object.keys(totalsByMonth).length}</td>
                <td className="px-3 py-3 text-right font-bold">{fmtAUD2(Object.values(totalsByMonth).reduce((a, b) => a + b, 0))}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────── Settings ─────────────────────────────── */
const SettingsField = ({ label, k, type = "text", suffix, step, value, onChange }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="relative">
      <Input
        type={type}
        value={value ?? ""}
        step={step}
        onChange={onChange}
        className="h-10 mt-1"
        data-testid={`setting-${k}`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState({ ...settings });
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm({ ...settings }), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gst_rate: Number(form.gst_rate),
        company_tax_rate: Number(form.company_tax_rate),
        director_wage_pct: Number(form.director_wage_pct),
        default_hourly_rate: Number(form.default_hourly_rate),
        default_hours_per_client_week: Number(form.default_hours_per_client_week),
        fy_start_month: Number(form.fy_start_month),
        currency: form.currency,
        company_name: form.company_name,
        abn: form.abn,
      };
      const r = await authFetch(`${API}/admin/finance/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("save failed");
      toast.success("Settings saved");
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-4">
        <SettingsIcon className="w-5 h-5 text-slate-500" />
        <h3 className="text-lg font-bold text-slate-900">Financial settings</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        These drive every calculation: P&L, GST, tax, director wages, and the forecast.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <SettingsField label="Company name" k="company_name" value={form.company_name} onChange={upd("company_name")} />
        <SettingsField label="ABN" k="abn" value={form.abn} onChange={upd("abn")} />
        <SettingsField label="GST rate" k="gst_rate" type="number" step="0.01" suffix="(0.10 = 10%)" value={form.gst_rate} onChange={upd("gst_rate")} />
        <SettingsField label="Company tax rate" k="company_tax_rate" type="number" step="0.01" suffix="(0.25 = 25%)" value={form.company_tax_rate} onChange={upd("company_tax_rate")} />
        <SettingsField label="Director wage % of profit" k="director_wage_pct" type="number" step="0.01" suffix="(0.40 = 40%)" value={form.director_wage_pct} onChange={upd("director_wage_pct")} />
        <SettingsField label="Default hourly rate" k="default_hourly_rate" type="number" step="1" suffix="AUD/hr" value={form.default_hourly_rate} onChange={upd("default_hourly_rate")} />
        <SettingsField label="Default hours / client / week" k="default_hours_per_client_week" type="number" step="0.5" value={form.default_hours_per_client_week} onChange={upd("default_hours_per_client_week")} />
        <SettingsField label="Financial year start month" k="fy_start_month" type="number" step="1" suffix="1–12 (AU = 7)" value={form.fy_start_month} onChange={upd("fy_start_month")} />
        <SettingsField label="Currency code" k="currency" suffix="ISO 4217" value={form.currency} onChange={upd("currency")} />
      </div>
      <div className="mt-6 flex items-center justify-end">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
